import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowUpRight,
    Heart,
    Sprout,
    GraduationCap,
    Utensils,
    HandHeart,
    Quote,
    Trees,
    BookOpen,
    Soup,
    Stethoscope,
    Users,
    Droplets,
    Lightbulb,
    HeartHandshake,
    Hand,
    Brush,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/useSettings";
import Counter from "@/components/Counter";

const ICONS = {
    tree: Trees,
    broom: Brush,
    droplet: Droplets,
    graduation: GraduationCap,
    book: BookOpen,
    lightbulb: Lightbulb,
    utensils: Utensils,
    soup: Soup,
    "heart-handshake": HeartHandshake,
    stethoscope: Stethoscope,
    users: Users,
    "hand-heart": Hand,
    leaf: Sprout,
};

const FOCUS = [
    {
        key: "environment",
        title: "Environment",
        icon: Sprout,
        desc: "Tree plantation, water conservation and cleanliness drives across Lucknow.",
        image: "https://images.pexels.com/photos/30574621/pexels-photo-30574621.jpeg?auto=compress&cs=tinysrgb&w=940",
    },
    {
        key: "education",
        title: "Education",
        icon: GraduationCap,
        desc: "Scholarships, learning circles and skill development for children & youth.",
        image: "https://images.pexels.com/photos/15119089/pexels-photo-15119089.jpeg?auto=compress&cs=tinysrgb&w=940",
    },
    {
        key: "food",
        title: "Food Relief",
        icon: Utensils,
        desc: "Community kitchens and daily food kit distribution for those in need.",
        image: "https://images.pexels.com/photos/6995244/pexels-photo-6995244.jpeg?auto=compress&cs=tinysrgb&w=940",
    },
    {
        key: "social",
        title: "Social Causes",
        icon: HandHeart,
        desc: "Health camps, women empowerment and community support programs.",
        image: "https://images.pexels.com/photos/6568957/pexels-photo-6568957.jpeg?auto=compress&cs=tinysrgb&w=940",
    },
];

const TESTIMONIALS = [
    {
        quote:
            "Tishya's scholarship gave my daughter wings. She's now in college and the first graduate in our family.",
        name: "Sunita Devi",
        role: "Parent · Vidya Daan",
    },
    {
        quote:
            "I joined a plantation drive on a Sunday and ended up volunteering every month. The team is real and on-ground.",
        name: "Rohan Mehrotra",
        role: "Volunteer",
    },
    {
        quote:
            "During the floods last year, Tishya was one of the first teams in our village with food and clean water.",
        name: "Mohammed Aslam",
        role: "Beneficiary · Anna Seva",
    },
];

export default function Home() {
    const { data: settings } = useSettings();
    const projectsQ = useQuery({ queryKey: ["projects"], queryFn: () => api.get("/projects").then((r) => r.data) });
    const galleryQ = useQuery({ queryKey: ["gallery"], queryFn: () => api.get("/gallery").then((r) => r.data) });

    const projects = (projectsQ.data || []).slice(0, 3);
    const gallery = (galleryQ.data || []).slice(0, 6);
    const s = settings || {};

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden grain">
                <div className="absolute inset-0 -z-10">
                    <img
                        src={s.hero_image}
                        alt=""
                        className="w-full h-full object-cover opacity-25"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" />
                </div>

                <div className="mx-auto max-w-7xl px-5 lg:px-10 pt-12 lg:pt-20 pb-16 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-7">
                        <div className="font-eyebrow text-xs text-secondary mb-5 flex items-center gap-3">
                            <span className="h-px w-10 bg-secondary inline-block" />
                            Charitable Trust · Lucknow, UP
                        </div>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                            Compassion in action,
                            <br />
                            <span className="text-secondary italic font-light">impact that lasts.</span>
                        </h1>
                        <p className="mt-7 max-w-xl text-base lg:text-lg text-muted-foreground leading-relaxed">
                            {s.hero_subtitle ||
                                "Tishya Foundation is a Lucknow-based charitable trust working at the intersection of environment, education, food security and social welfare."}
                        </p>
                        <div className="mt-9 flex flex-wrap items-center gap-3">
                            <Link to="/donate">
                                <Button
                                    data-testid="hero-donate-button"
                                    size="lg"
                                    className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground gap-2 h-12 px-7"
                                >
                                    Donate Now <Heart className="h-4 w-4" />
                                </Button>
                            </Link>
                            <Link to="/volunteer">
                                <Button
                                    data-testid="hero-volunteer-button"
                                    size="lg"
                                    variant="outline"
                                    className="rounded-full border-foreground/30 hover:border-primary h-12 px-7 gap-2"
                                >
                                    Become a Volunteer <ArrowUpRight className="h-4 w-4" />
                                </Button>
                            </Link>
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="grid grid-cols-2 gap-3">
                            <img
                                src="https://images.pexels.com/photos/15119089/pexels-photo-15119089.jpeg?auto=compress&cs=tinysrgb&w=600"
                                alt="Education"
                                className="row-span-2 h-72 lg:h-96 w-full object-cover"
                                loading="lazy"
                            />
                            <img
                                src="https://images.pexels.com/photos/2382665/pexels-photo-2382665.jpeg?auto=compress&cs=tinysrgb&w=600"
                                alt="Plantation"
                                className="h-36 lg:h-[11.75rem] w-full object-cover"
                                loading="lazy"
                            />
                            <img
                                src="https://images.pexels.com/photos/6995244/pexels-photo-6995244.jpeg?auto=compress&cs=tinysrgb&w=600"
                                alt="Food"
                                className="h-36 lg:h-[11.75rem] w-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>

                {/* Editorial ribbon */}
                <div className="overflow-hidden border-y border-border/60 bg-accent/40 py-6">
                    <div className="marquee whitespace-nowrap font-display text-3xl lg:text-5xl text-primary/80">
                        {Array(2).fill(0).map((_, i) => (
                            <div key={i} className="flex gap-24 items-center pr-24">
                                <span>Environment</span>
                                <span className="text-secondary">✦</span>
                                <span>Education</span>
                                <span className="text-secondary">✦</span>
                                <span>Food Relief</span>
                                <span className="text-secondary">✦</span>
                                <span>Community</span>
                                <span className="text-secondary">✦</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ABOUT short */}
            <section className="mx-auto max-w-7xl px-5 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-5">
                    <div className="font-eyebrow text-xs text-secondary mb-4">About the foundation</div>
                    <h2 className="font-display text-3xl lg:text-5xl leading-tight">
                        Built on trust,
                        <br />
                        powered by community.
                    </h2>
                </div>
                <div className="lg:col-span-7 lg:pt-3">
                    <p className="text-lg leading-relaxed text-foreground/80">
                        {s.about_short}
                    </p>
                    <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                        Established in Lucknow, Tishya Foundation works on the ground — planting native
                        trees, supporting schoolchildren, running community kitchens and organising
                        free health camps. Every rupee is tracked, every story is documented.
                    </p>
                    <Link
                        to="/about"
                        className="mt-6 inline-flex items-center gap-1.5 text-primary font-medium underline-anim"
                        data-testid="home-about-more"
                    >
                        Read our story <ArrowUpRight className="h-4 w-4" />
                    </Link>
                </div>
            </section>

            {/* FOCUS AREAS */}
            <section className="bg-muted/30 border-y border-border/60">
                <div className="mx-auto max-w-7xl px-5 lg:px-10 py-20">
                    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
                        <div>
                            <div className="font-eyebrow text-xs text-secondary mb-3">Focus areas</div>
                            <h2 className="font-display text-3xl lg:text-5xl tracking-tight max-w-2xl">
                                Where we put our hearts, hours and rupees.
                            </h2>
                        </div>
                        <Link
                            to="/programs"
                            className="hidden lg:inline-flex items-center gap-1 text-sm font-medium text-primary underline-anim"
                        >
                            All programs <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {FOCUS.map((f, idx) => (
                            <Link
                                to="/programs"
                                key={f.key}
                                data-testid={`focus-card-${f.key}`}
                                className="group bg-card border border-border hover:border-primary/40 transition-all duration-300 hover:-translate-y-1"
                            >
                                <div className="aspect-[4/3] overflow-hidden">
                                    <img
                                        src={f.image}
                                        alt={f.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <div className="p-5">
                                    <div className="flex items-center gap-2 mb-2">
                                        <f.icon className="h-5 w-5 text-secondary" />
                                        <span className="font-eyebrow text-[10px] text-muted-foreground">0{idx + 1}</span>
                                    </div>
                                    <h3 className="font-display text-xl">{f.title}</h3>
                                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* IMPACT COUNTERS */}
            <section className="mx-auto max-w-7xl px-5 lg:px-10 py-20 lg:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <div className="font-eyebrow text-xs text-secondary mb-3">Our impact, so far</div>
                        <h2 className="font-display text-3xl lg:text-5xl leading-tight">
                            Numbers we are proud of,
                            <span className="text-secondary"> not just numbers.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-8 grid grid-cols-2 gap-px bg-border border border-border">
                        {[
                            { label: "Beneficiaries reached", value: s.counter_beneficiaries || 12500, suffix: "+" },
                            { label: "Trees planted", value: s.counter_trees || 18400, suffix: "+" },
                            { label: "Food kits distributed", value: s.counter_food_kits || 9600, suffix: "+" },
                            { label: "Programs conducted", value: s.counter_programs || 84, suffix: "" },
                        ].map((c) => (
                            <div key={c.label} className="bg-background p-6 lg:p-8">
                                <div className="font-display text-5xl lg:text-6xl text-primary tracking-tight" data-testid={`counter-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
                                    <Counter to={c.value} suffix={c.suffix} />
                                </div>
                                <div className="mt-2 text-sm text-muted-foreground font-eyebrow text-xs">{c.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* RECENT PROJECTS */}
            <section className="bg-primary text-primary-foreground">
                <div className="mx-auto max-w-7xl px-5 lg:px-10 py-20">
                    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
                        <div>
                            <div className="font-eyebrow text-xs text-secondary mb-3">Recent projects</div>
                            <h2 className="font-display text-3xl lg:text-5xl tracking-tight max-w-2xl">
                                Work in motion.
                            </h2>
                        </div>
                        <Link to="/projects" className="text-sm font-medium underline-anim">
                            See all projects →
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {projects.map((p) => (
                            <Link
                                key={p.id}
                                to={`/projects/${p.slug}`}
                                data-testid={`home-project-${p.slug}`}
                                className="group bg-primary-foreground/5 border border-primary-foreground/20 hover:bg-primary-foreground/10 transition-colors"
                            >
                                <div className="aspect-[4/3] overflow-hidden">
                                    <img
                                        src={p.cover_image}
                                        alt={p.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <div className="p-5">
                                    <div className="font-eyebrow text-[10px] text-secondary mb-2">{p.status}</div>
                                    <h3 className="font-display text-xl leading-tight">{p.title}</h3>
                                    <p className="mt-2 text-sm text-primary-foreground/80 line-clamp-2">{p.description}</p>
                                </div>
                            </Link>
                        ))}
                        {projects.length === 0 && (
                            <div className="md:col-span-3 text-primary-foreground/70 text-sm">
                                Projects will appear here soon.
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* GALLERY PREVIEW */}
            <section className="mx-auto max-w-7xl px-5 lg:px-10 py-20">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
                    <div>
                        <div className="font-eyebrow text-xs text-secondary mb-3">From the field</div>
                        <h2 className="font-display text-3xl lg:text-5xl tracking-tight">Faces, places, moments.</h2>
                    </div>
                    <Link to="/gallery" className="text-sm font-medium text-primary underline-anim">
                        Full gallery →
                    </Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {gallery.map((g, i) => (
                        <div
                            key={g.id}
                            className={`overflow-hidden group ${i === 0 ? "md:col-span-2 md:row-span-2" : ""}`}
                            data-testid={`home-gallery-${g.id}`}
                        >
                            <img
                                src={g.image_url}
                                alt={g.title}
                                loading="lazy"
                                className={`w-full object-cover group-hover:scale-105 transition-transform duration-700 ${i === 0 ? "aspect-square md:aspect-auto md:h-full" : "aspect-square"}`}
                            />
                        </div>
                    ))}
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="bg-muted/30 border-y border-border/60">
                <div className="mx-auto max-w-7xl px-5 lg:px-10 py-20">
                    <div className="font-eyebrow text-xs text-secondary mb-3">Voices of impact</div>
                    <h2 className="font-display text-3xl lg:text-5xl tracking-tight mb-12 max-w-2xl">
                        Quiet stories that keep us going.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {TESTIMONIALS.map((t, i) => (
                            <div key={i} className="bg-background border border-border p-6 lg:p-8">
                                <Quote className="h-6 w-6 text-secondary mb-4" />
                                <p className="text-base leading-relaxed">{t.quote}</p>
                                <div className="mt-6 pt-4 border-t border-border">
                                    <div className="font-display text-base">{t.name}</div>
                                    <div className="text-xs text-muted-foreground font-eyebrow">{t.role}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="mx-auto max-w-7xl px-5 lg:px-10 py-24">
                <div className="bg-primary text-primary-foreground p-10 lg:p-16 grain relative overflow-hidden">
                    <div className="max-w-3xl">
                        <div className="font-eyebrow text-xs text-secondary mb-4">Join us</div>
                        <h2 className="font-display text-3xl lg:text-5xl leading-tight">
                            Donate. Volunteer. Partner.
                            <br />
                            <span className="italic font-light">Be part of the change.</span>
                        </h2>
                        <p className="mt-5 text-primary-foreground/80 max-w-xl leading-relaxed">
                            Whether it's a one-time donation, a Sunday volunteering, or a long-term
                            CSR partnership — your contribution multiplies on the ground.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/donate">
                                <Button data-testid="cta-donate" size="lg" className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground h-12 px-7">
                                    Donate now
                                </Button>
                            </Link>
                            <Link to="/volunteer">
                                <Button data-testid="cta-volunteer" size="lg" variant="outline" className="rounded-full border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground hover:text-primary h-12 px-7">
                                    Become a volunteer
                                </Button>
                            </Link>
                            <Link to="/contact">
                                <Button data-testid="cta-partner" size="lg" variant="ghost" className="rounded-full text-primary-foreground hover:bg-primary-foreground/10 h-12 px-7">
                                    Partner with us →
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
